import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Mail,
  Phone,
  Globe,
  Award,
  ChevronLeft,
  FileCheck,
  BookOpen,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PublicFooter from '../components/PublicFooter';
import SEO, { generateInstructorStructuredData } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function PublicNav() {
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/public" className="flex items-center gap-2">
            <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
              <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                DealLab
              </span>
              <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Courses
            </Link>
            <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Schedule
            </Link>
            <Link to="/public/instructors" className="text-[#1E3A5F] font-medium">
              Instructors
            </Link>
            <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
              Become a Sponsor
            </Link>
          </div>
          
          <Link to="/public/request">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white">
              Book for Your Team
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function InstructorProfilePage() {
  const { instructorId } = useParams();
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInstructor();
  }, [instructorId]);

  const fetchInstructor = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/public/instructors/${instructorId}`);
      setInstructor(response.data);
    } catch (err) {
      console.error('Error fetching instructor:', err);
      setError('Instructor not found');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || !instructor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Instructor Not Found</h3>
              <p className="text-slate-500 mb-6">
                The instructor you're looking for doesn't exist or has been removed.
              </p>
              <Link to="/public/instructors">
                <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back to Instructors
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`${instructor.name} - CE Instructor`}
        description={instructor.bio || `${instructor.name} is a certified CE instructor at DealLab CE, teaching TREC-approved continuing education courses for Texas real estate agents.`}
        keywords={`${instructor.name}, CE instructor, real estate instructor, Texas CE teacher`}
        url={`/public/instructors/${instructor.id}`}
        structuredData={generateInstructorStructuredData(instructor)}
      />
      <PublicNav />
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link to="/public/instructors">
          <Button variant="ghost" className="text-slate-600 hover:text-[#1E3A5F]">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to All Instructors
          </Button>
        </Link>
      </div>

      {/* Profile Header */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header Banner */}
            <div className="h-32 bg-gradient-to-r from-[#1E3A5F] to-sky-600"></div>
            
            <div className="px-8 pb-8">
              {/* Photo */}
              <div className="-mt-16 mb-6">
                {instructor.photo_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${instructor.photo_url}`}
                    alt={instructor.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-32 h-32 rounded-full bg-gradient-to-br from-[#1E3A5F] to-sky-600 flex items-center justify-center text-white text-4xl font-bold border-4 border-white shadow-lg ${instructor.photo_url ? 'hidden' : ''}`}
                >
                  {getInitials(instructor.name)}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Info */}
                <div className="lg:col-span-2">
                  <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {instructor.name}
                  </h1>
                  {instructor.title && (
                    <p className="text-lg text-slate-600 mb-4">{instructor.title}</p>
                  )}

                  {/* Specialties */}
                  {instructor.specialties && instructor.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {instructor.specialties.map((specialty, idx) => (
                        <Badge 
                          key={idx}
                          className="bg-sky-100 text-sky-700 hover:bg-sky-200 border-0"
                        >
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Bio */}
                  {instructor.bio && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-[#1E3A5F] mb-3">About</h2>
                      <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                        {instructor.bio}
                      </p>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Contact Card */}
                  <Card className="border-slate-200">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-[#1E3A5F] mb-4">Contact Information</h3>
                      <div className="space-y-3">
                        {instructor.email && (
                          <a 
                            href={`mailto:${instructor.email}`}
                            className="flex items-center gap-3 text-slate-600 hover:text-sky-600 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Mail className="w-5 h-5" />
                            </div>
                            <span className="text-sm">{instructor.email}</span>
                          </a>
                        )}
                        {instructor.phone && (
                          <a 
                            href={`tel:${instructor.phone}`}
                            className="flex items-center gap-3 text-slate-600 hover:text-sky-600 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Phone className="w-5 h-5" />
                            </div>
                            <span className="text-sm">{instructor.phone}</span>
                          </a>
                        )}
                        {instructor.website && (
                          <a 
                            href={instructor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-slate-600 hover:text-sky-600 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Globe className="w-5 h-5" />
                            </div>
                            <span className="text-sm">Visit Website</span>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Certification Card */}
                  {instructor.certification_pdf_url && (
                    <Card className="border-emerald-200 bg-emerald-50/50">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Award className="w-5 h-5 text-emerald-600" />
                          <h3 className="font-semibold text-emerald-800">PSI Certified Instructor</h3>
                        </div>
                        <p className="text-sm text-emerald-700 mb-4">
                          This instructor is certified by Preferred Systems Inc. to teach TREC-approved continuing education courses.
                        </p>
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${instructor.certification_pdf_url}`, '_blank')}
                          data-testid="view-certification-btn"
                        >
                          <FileCheck className="w-4 h-4 mr-2" />
                          View Instructor Approval Letter
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Stats */}
                  <Card className="border-slate-200">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-[#1E3A5F] mb-4">Credentials</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-sky-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">TREC Approved</p>
                            <p className="text-xs text-slate-500">CE Instructor</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Award className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">Preferred Systems</p>
                            <p className="text-xs text-slate-500">Provider #9980</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Book a Course with {instructor.name.split(' ')[0]}
          </h2>
          <p className="text-slate-600 mb-6">
            Request a private CE course for your team or brokerage taught by this expert instructor.
          </p>
          <Link to="/public/request">
            <Button size="lg" className="bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white px-8">
              Request a Course
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
